import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, Loader2Icon, RotateCcwIcon } from 'lucide-react';
import { signupFrequencies, signupTopics } from '../../data/results';
interface SignUpScreenProps {
  onRestart: () => void;
}
type Status = 'idle' | 'submitting' | 'done';
const ease = [0.22, 1, 0.36, 1] as const;
export function SignUpScreen({
  onRestart
}: SignUpScreenProps) {
  const [topics, setTopics] = useState<string[]>([signupTopics[0]]);
  const [frequency, setFrequency] = useState(signupFrequencies[0].id);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = emailValid && consent && topics.length > 0;
  const toggleTopic = (t: string) => setTopics(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setStatus('submitting');
    // Prototype only: nothing is stored or sent.
    timer.current = window.setTimeout(() => {
      setStatus('done');
      setEmail('');
    }, 900);
  };
  return <div className="h-full overflow-y-auto bg-mist-50 px-6 pb-10 pt-24 no-scrollbar md:pt-28" data-id="element-681" data-mp="zh16on7hgtih">
      <AnimatePresence mode="wait" initial={false} data-id="element-682" data-mp="1dgj0xi3jsyz8">
        {status === 'done' ? <motion.div key="done" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease
      }} className="flex min-h-full flex-col" role="status" data-id="element-683" data-mp="18i7s9aozouil">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-pulse-teal/10" data-id="element-684" data-mp="1e6pkfbjfz6v0">
              <CheckCircle2Icon className="h-7 w-7 text-pulse-teal" data-id="element-685" data-mp="1j5slym07quy8" />
            </div>
            <h2 className="mt-5 font-serif text-[30px] leading-tight text-navy-900" data-id="element-686" data-mp="1j7na096s736j">You’re part of the next conversation.</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-500" data-id="element-687" data-mp="1s70cghwhmrhr">
              We’d invite you to future IVF Pulse experiences on {topics.join(', ').toLowerCase()}.
            </p>
            <p className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-[12px] leading-relaxed text-amber-900" data-id="element-688" data-mp="224esigei1e9j">
              Prototype only — no details have been stored or sent. A live version would require approved consent
              wording, privacy notice and sponsor disclosure.
            </p>
            <button type="button" onClick={onRestart} className="mx-auto mt-auto flex items-center gap-2 rounded-full px-4 py-6 text-[13px] text-slate-500 transition hover:text-navy-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-900/30" data-id="element-689" data-mp="2fwupp0kpyi2j">
              <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-690" data-mp="2pz8f58t4w413" />
              Replay the Results Snak
            </button>
          </motion.div> : <motion.form key="form" onSubmit={handleSubmit} noValidate exit={{
        opacity: 0,
        y: -8
      }} data-id="element-691" data-mp="2gzbkmqk2cay4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pulse-teal" data-id="element-692" data-mp="3dpksiz75esz2">Stay in the conversation</p>
            <h2 className="mt-2 font-serif text-[28px] leading-[1.12] text-navy-900" data-id="element-693" data-mp="38q7wbnue09jt">
              Join future IVF Pulse experiences.
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-slate-500" data-id="element-694" data-mp="33xojit3ixig2">
              A few minutes, a few times a year — and you always see the results first.
            </p>

            <fieldset className="mt-6" data-id="element-695" data-mp="c8zyl3i6m5s">
              <legend className="text-[12px] font-semibold text-navy-900" data-id="element-696" data-mp="3rpdigsaup55z">Topics that interest you</legend>
              <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-697" data-mp="3c7luqcyg50bk">
                {signupTopics.map(t => {
              const on = topics.includes(t);
              return <button key={t} type="button" onClick={() => toggleTopic(t)} aria-pressed={on} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-teal ${on ? 'border-navy-900 bg-navy-900 text-white' : 'border-navy-900/15 bg-white text-navy-800 hover:border-navy-900/30'}`} data-id="element-698" data-mp="336nqbtk3iqsg">
                      {on && <CheckIcon className="h-3.5 w-3.5" data-id="element-699" data-mp="3q5aj3urq2pld" />}
                      {t}
                    </button>;
            })}
              </div>
              {touched && topics.length === 0 && <p className="mt-1.5 text-[12px] text-red-600" data-id="element-700" data-mp="mjv3fxutodgd">Choose at least one topic.</p>}
            </fieldset>

            <fieldset className="mt-5" data-id="element-701" data-mp="229vuq4vn6td5">
              <legend className="text-[12px] font-semibold text-navy-900" data-id="element-702" data-mp="2292mezh1bvsw">How often</legend>
              <div className="mt-2.5 grid grid-cols-2 gap-1 rounded-full bg-mist-200 p-1" data-id="element-703" data-mp="2gxpem1rrgi1j">
                {signupFrequencies.map(f => <label key={f.id} className={`cursor-pointer rounded-full px-3 py-2 text-center text-[12px] font-medium transition ${frequency === f.id ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500'}`} data-id="element-704" data-mp="1o9l7dbtjjwwq">
                    <input type="radio" name="frequency" value={f.id} checked={frequency === f.id} onChange={() => setFrequency(f.id)} className="sr-only" data-id="element-705" data-mp="1t84s58wef2ni" />
                    {f.label}
                  </label>)}
              </div>
            </fieldset>

            <div className="mt-5" data-id="element-706" data-mp="3got43j611dxh">
              <label htmlFor="pulse-email" className="text-[12px] font-semibold text-navy-900" data-id="element-707" data-mp="3gs744kr7xb1r">
                Work email
              </label>
              <input id="pulse-email" type="email" inputMode="email" autoComplete="off" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@clinic.org" aria-invalid={touched && !emailValid} className="mt-2 h-12 w-full rounded-2xl border border-navy-900/15 bg-white px-4 text-[15px] text-navy-900 placeholder:text-slate-400 focus:border-pulse-teal focus:outline-none focus:ring-2 focus:ring-pulse-teal/20" data-id="element-708" data-mp="3qok0xri2850q" />
              {touched && !emailValid && <p className="mt-1.5 text-[12px] text-red-600" data-id="element-709" data-mp="2f7k5in0f1a2h">Enter a valid email address.</p>}
            </div>

            <label className="mt-4 flex cursor-pointer items-start gap-3" data-id="element-710" data-mp="wbo5gk12jb29">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-navy-900/30 accent-navy-900" data-id="element-711" data-mp="sd6vlsq9jmh5" />
              <span className="text-[12px] leading-relaxed text-slate-500" data-id="element-712" data-mp="dfegv2sjj240">
                I agree to be contacted about future IVF Pulse experiences. Consent wording is illustrative and would be
                approved before launch.
              </span>
            </label>
            {touched && !consent && <p className="mt-1.5 text-[12px] text-red-600" data-id="element-713" data-mp="3vtxakx3tp9uz">Please confirm to continue.</p>}

            <button type="submit" disabled={status === 'submitting'} className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-navy-900 text-[15px] font-semibold text-white transition hover:bg-navy-800 disabled:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-teal focus-visible:ring-offset-2" data-id="element-714" data-mp="3quaumfwm1fih">
              {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-715" data-mp="14l1zwqi2ualb" />}
              {status === 'submitting' ? 'Joining…' : 'Join the IVF Pulse panel'}
            </button>
            <p className="mt-3 text-center text-[11px] text-slate-400" data-id="element-716" data-mp="uv8sm1oywhmv">Prototype · nothing is stored or sent</p>
          </motion.form>}
      </AnimatePresence>
    </div>;
}